import React, { useState, useEffect } from "react";

//#region Material Components
import { withStyles } from "@material-ui/styles";
import MuiDialog from "@material-ui/core/Dialog";
import MuiDialogContent from "@material-ui/core/DialogContent";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import Divider from "@material-ui/core/Divider";
import { CircularProgress } from "@material-ui/core";
//#endregion

//#region Internal
import ApproveButton from "./ApproveButton";
import { Primary } from "../../assets/Colors";
import {
  getImageUrlR,
  cropPage,
} from "../../api/FeedBacks";
import { getCMSReportDetails } from "../../api/CMSReports";
import { getEditorialSystemId } from "../../api/BookSectionCrop";
import EditorialSystemId from "../../enums/EditorialSystemId";
import ResponseStatus from "../../enums/ResponseStatus";
//#endregion

// Trimmed clone of FeedBackEditDialog's BookSectionCrop/cropId branch, dedicated
// to the CMS Reports page. Keeps the dev/prod images, answer change (A–E),
// Share Link, Open Video, Go To Crop Page and the reports list.
// Intentionally omits: Mark As Done, Delete Video, Delete Question.

const Dialog = withStyles({
  paperWidthSm: {
    maxWidth: 920,
    minWidth: 300,
    minHeight: 220,
  },
})(MuiDialog);

const DialogContent = withStyles({
  root: {
    paddingTop: 0,
    display: "flex",
    flexDirection: "column",
    width: "50%",
  },
})(MuiDialogContent);

const DialogTitle = withStyles({
  root: {
    marginLeft: 20,
  },
})(MuiDialogTitle);
const formatDateTime = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};
function CMSReportEditDialog(props) {
  const { user, isOpen, onClose, selectedCropId } = props;

  const [details, setDetails] = useState([]);
  const [cropIdDetails, setCropIdDetails] = useState(null);
  const [imageUrl, setImageUrl] = useState({
    mainImage: null,
    imageParts: [],
    RowStatus: false,
  });
  const [editorialSystemIdInfo, setEditorialSystemIdInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const correct = cropIdDetails ? cropIdDetails.AnswerOption : "";

  // ─── Loaders ────────────────────────────────────────────────────────────────

  const getCropDetails = async () => {
    const res = await cropPage(Number(selectedCropId), user.Token);
    if (res.status === 200) {
      setCropIdDetails(res.data);
    } else {
      onClose();
    }
  };

  const getReportRows = async () => {
    const res = await getCMSReportDetails(selectedCropId, user.Token);
    if (res.status === 200 && res.data.ResponseStatus == ResponseStatus.Success) {
      setDetails(
        Array.isArray(res.data?.ResponseData) ? res.data.ResponseData : []
      );
    }
  };

  const fetchImageUrl = async () => {
    if (!cropIdDetails) return;
    const param = {
      BookSectionId: cropIdDetails.BookSectionId,
      QuestionNumber: cropIdDetails.QuestionNumber,
    };
    try {
      // These questions don't exist in PROD, so we only fetch the DEV image.
      const devRes = await getImageUrlR(param, user.Token);
      if (devRes && devRes.status === 200) {
        const { ImageUrl, ImagePartUrls, RowStatus } = devRes.data;
        setImageUrl({
          mainImage: ImageUrl,
          imageParts: ImagePartUrls || [],
          RowStatus: RowStatus,
        });
      }
    } catch (error) {
      console.error("CMSReportEditDialog fetchImageUrl error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchEditorialSystemId = async () => {
    if (!cropIdDetails) return;
    try {
      const res = await getEditorialSystemId(
        cropIdDetails.BookSectionId,
        user.Token
      );
      if (res.status === 200) {
        setEditorialSystemIdInfo(res.data.ResponseData);
      }
    } catch (error) {
      console.error("CMSReportEditDialog fetchEditorialSystemId error:", error);
    }
  };

  // ─── Effects ────────────────────────────────────────────────────────────────

  // When opened with a crop id, (re)load its details + report rows.
  useEffect(() => {
    if (!isOpen || !selectedCropId) return;
    setCropIdDetails(null);
    setDetails([]);
    setImageUrl({ mainImage: null, imageParts: [], RowStatus: false });
    setEditorialSystemIdInfo(null);
    setIsLoading(true);
    getCropDetails();
    getReportRows();
  }, [isOpen, selectedCropId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Once crop details arrive, load the images + editorial system id.
  useEffect(() => {
    if (!isOpen || !cropIdDetails) return;
    fetchImageUrl();
    fetchEditorialSystemId();
  }, [cropIdDetails]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Handlers ───────────────────────────────────────────────────────────────

  const shareLink = async () => {
    if (!cropIdDetails) return;
    const param = {
      BookSectionId: cropIdDetails.BookSectionId,
      QuestionNumber: cropIdDetails.QuestionNumber,
    };
    const res = await getImageUrlR(param, user.Token);
    if (res.status === 200) {
      let url = "https://product.pakodemy.com/share/index.htm?q=";
      let q = res.data.ImageUrl.substring(res.data.ImageUrl.indexOf("crops") + 6);
      const part = res.data.ImagePartUrls && res.data.ImagePartUrls[0];
      if (part) q += `__${part.substring(part.indexOf("crops") + 6)}`;
      url += q;
      const w = window.open(url, "_blank", "noopener,noreferrer");
      if (w) w.opener = null;
    }
  };

  const openVideo = () => {
    if (!cropIdDetails || !cropIdDetails.AnswerVideoPath) return;
    const w = window.open(
      cropIdDetails.AnswerVideoPath,
      "_blank",
      "noopener,noreferrer"
    );
    if (w) w.opener = null;
  };

  const goToCropPage = () => {
    if (!cropIdDetails) return;
    localStorage.setItem("bookSectionId", cropIdDetails.BookSectionId);
    localStorage.setItem("targetQuestion", cropIdDetails.QuestionNumber);
    sessionStorage.setItem("fromFeedBack", true);
    const w = window.open("/booksectioncrop", "_blank");
    if (w) w.opener = null;
  };

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <Dialog id={props.id} open={isOpen} onClose={onClose}>
      <div style={styles.generalContainer}>
        {/* Left: question image (DEV only — these questions aren't in PROD) */}
        <div style={{ backgroundColor: Primary.Background }}>
          <DialogTitle>İşlemler</DialogTitle>
          <div>
            {isLoading ? (
              <div style={{ display: "flex", justifyContent: "center", padding: 20 }}>
                <CircularProgress />
              </div>
            ) : (
              <div style={styles.envSection}>
                {imageUrl.mainImage ? (
                  <>
                    <img style={styles.questionImg} src={imageUrl.mainImage} alt="Soru" />
                    {imageUrl.imageParts.map((partUrl, index) => (
                      <img
                        key={index}
                        src={partUrl}
                        alt={`Parça ${index + 1}`}
                        style={styles.questionImg}
                      />
                    ))}
                  </>
                ) : (
                  <div style={styles.envEmpty}>Görsel bulunamadı</div>
                )}
              </div>
            )}
          </div>
          <Divider />
        </div>

        {/* Right: answer, actions, reports list */}
        <DialogContent>
          <div style={styles.answerBox}>
            Doğru Cevap:{" "}
            <span style={styles.answerValue}>{correct || "—"}</span>
          </div>

          {editorialSystemIdInfo === EditorialSystemId.CropPage && (
            <ApproveButton key="btn-crop" onClick={goToCropPage} text="Go To Crop Page" />
          )}
          <ApproveButton key="btn-share" onClick={shareLink} text="Share Link" />
          {cropIdDetails && cropIdDetails.AnswerVideoPath && (
            <ApproveButton key="btn-video" onClick={openVideo} text="Open Video" />
          )}

          {!imageUrl.RowStatus && !isLoading && cropIdDetails && (
            <div style={{ color: "red", margin: "2%" }}>Bu Soru Silinmiştir</div>
          )}

          <div style={styles.reportsHeader}>
            Raporlar ({details.length})
          </div>
          <Divider />
          <div style={{ scrollBehavior: "smooth", marginTop: 8 }}>
            {details.length === 0 ? (
              <div style={{ color: "#9ca3af", fontSize: 13, padding: "8px 0" }}>
                Rapor detayı bulunamadı.
              </div>
            ) : (
              details.map((item) => (
                <div key={item.Id} style={styles.reportCard}>
                  <div style={styles.reportHead}>
                    <span style={styles.reportUser}>Kullanıcı #{item.UserId}</span>
                    <span style={styles.reportDate}>
                      {formatDateTime(item.CreatedDate)}
                    </span>
                  </div>
                  {Array.isArray(item.IssueTypeNames) &&
                    item.IssueTypeNames.length > 0 && (
                      <div style={styles.chipRow}>
                        {item.IssueTypeNames.map((name, i) => (
                          <span key={i} style={styles.chip}>
                            {name}
                          </span>
                        ))}
                      </div>
                    )}
                  {item.Message ? (
                    <div style={styles.reportMsg}>{item.Message}</div>
                  ) : null}
                </div>
                 ))
                 )}
          </div>
        </DialogContent>
      </div>
    </Dialog>
  );
}

const styles = {
  generalContainer: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
  },
  reportsHeader: {
    marginTop: 12,
    fontWeight: 700,
    fontSize: 14,
    color: "#111827",
  },
  reportCard: {
    padding: "8px 0",
    borderBottom: "1px solid #eef0f2",
  },
  reportHead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 8,
  },
  reportUser: {
    fontSize: 13,
    fontWeight: 600,
    color: "#374151",
  },
  reportDate: {
    fontSize: 11,
    color: "#9ca3af",
    whiteSpace: "nowrap",
  },
  chipRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    marginTop: 6,
  },
  chip: {
    backgroundColor: "#eff6ff",
    color: "#1d4ed8",
    border: "1px solid #bfdbfe",
    borderRadius: 12,
    padding: "2px 10px",
    fontSize: 12,
    fontWeight: 600,
  },
  reportMsg: {
    marginTop: 6,
    fontSize: 13,
    color: "#374151",
    backgroundColor: "#f9fafb",
    borderRadius: 6,
    padding: "6px 10px",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  answerBox: {
    fontSize: 15,
    fontWeight: 600,
    color: "#374151",
    margin: "12px 0",
    display: "flex",
    alignItems: "center",
  },
  answerValue: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 28,
    height: 28,
    padding: "0 8px",
    marginLeft: 6,
    borderRadius: 6,
    backgroundColor: Primary.TopBar,
    color: "#fff",
    fontWeight: 700,
  },
  questionImg: {
    maxWidth: "100%",
    maxHeight: "300px",
    objectFit: "contain",
  },
  envSection: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "8px 0",
  },
  envEmpty: {
    color: "#9e9e9e",
    fontSize: 13,
    padding: "12px 8px",
  },
};

export default CMSReportEditDialog;
